import { Frequency } from "generated-graphql/graphql";
import { partition } from "lodash-es";

export interface PartitionedSubmissions<T> {
  realSubmissions: T[];
  virtualEvents: T[];
  uniqueSubmissionIds: string[];
  virtualEventIds: string[];
}

/**
 * Returns true if the row represents a materialized submission (instance or standalone),
 * false if it is a virtual event from a template.
 *
 * Prefers TaskKind when available: INSTANCE/STANDALONE = materialized, TEMPLATE = virtual.
 * Falls back to frequency when kind is not yet in the GraphQL schema.
 * TODO: Remove frequency fallback once kind is added to VirtualEvent and
 * WasteVirtualEventForStaff in schema.graphql.
 */
export function isRealSubmission<T>(
  row: T,
  getFrequency: (row: T) => string | null | undefined
): boolean {
  const rowWithKind = row as T & { kind?: string };
  if (rowWithKind.kind != null) {
    return rowWithKind.kind !== "TEMPLATE";
  }
  return getFrequency(row) === Frequency.Once;
}

/**
 * Partitions submissions into materialized submissions and virtual events based on task kind.
 *
 * - Materialized (kind !== TEMPLATE): real submissions with their own Task + Submission record.
 * - Virtual events (kind === TEMPLATE): computed occurrences from recurring tasks that share
 *   a template Submission record and need materialization before they can be worked on.
 *
 * NOTE: We cannot rely on submission.id existing to detect virtual events because
 * template submissions are created upfront by the create-submission-tasks lambda.
 */
export function partitionSubmissions<T extends { id: string }>(
  submissions: T[],
  opts: {
    getFrequency: (row: T) => string | null | undefined;
    getSubmissionId: (row: T) => string | null | undefined;
  }
): PartitionedSubmissions<T> {
  const [realSubmissions, virtualEvents] = partition(submissions, (row) =>
    isRealSubmission(row, opts.getFrequency)
  );

  const uniqueSubmissionIds = [
    ...new Set(realSubmissions.map(opts.getSubmissionId).filter((id): id is string => Boolean(id))),
  ];

  const virtualEventIds = virtualEvents.map((s) => s.id);

  return {
    realSubmissions,
    virtualEvents,
    uniqueSubmissionIds,
    virtualEventIds,
  };
}
